import React from 'react';
import {
  Paper,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Collapse,
  Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SuccessAlert from '../../Common/SuccessAlert';
import MathProblemsForm from './MathProblemsForm';
import MathProblemsDisplay from './MathProblemsDisplay';
import { useMathProblems } from './hooks/useMathProblems';

const MathWordProblemsPageView: React.FC = () => {
  const p = useMathProblems();

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <SuccessAlert
        open={p.showSuccessAlert}
        message={p.successMessage}
        onClose={() => p.setShowSuccessAlert(false)}
      />

      {p.error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => p.setError(null)}>
          {p.error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Generate Math Word Problems
          </Typography>
          <IconButton onClick={() => p.setIsFormCollapsed(!p.isFormCollapsed)}>
            {p.isFormCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Box>

        <Collapse in={!p.isFormCollapsed}>
          <MathProblemsForm
            handleSubmit={p.handleSubmit}
            gradeLevel={p.gradeLevel}
            setGradeLevel={p.setGradeLevel}
            availableGradeLevels={p.availableGradeLevels}
            gradeLevels={p.GRADE_LEVELS}
            selectedStrand={p.selectedStrand}
            setSelectedStrand={p.setSelectedStrand}
            setSelectedStandards={p.setSelectedStandards}
            availableStrands={p.availableStrands}
            strandFilteredStandards={p.strandFilteredStandards}
            selectedStandards={p.selectedStandards}
            standardLoading={p.standardLoading}
            numQuestions={p.numQuestions}
            setNumQuestions={p.setNumQuestions}
            language={p.language}
            setLanguage={p.setLanguage}
            questionStyle={p.questionStyle}
            setQuestionStyle={p.setQuestionStyle}
            questionMix={p.questionMix}
            setQuestionMix={p.setQuestionMix}
            isStreaming={p.isStreaming}
            isLoading={p.isLoading}
            handleCancelGeneration={p.handleCancelGeneration}
          />
        </Collapse>
      </Paper>

      {p.currentStatus && (
        <Paper elevation={2} sx={{ p: 2.5, mb: 3, backgroundColor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#374151' }}>
              {p.currentStatus}
            </Typography>
          </Box>
        </Paper>
      )}

      <MathProblemsDisplay
        isStreaming={p.isStreaming}
        streamingQuestionsContent={p.streamingQuestionsContent}
        streamingAnswerKeyContent={p.streamingAnswerKeyContent}
        questions={p.questions}
        answerKey={p.answerKey}
        gradeLevel={p.gradeLevel}
        graphCount={p.graphCount}
        error={p.error}
        anchorEl={p.anchorEl}
        open={p.open}
        isSaving={p.isSaving}
        isCapturingGraphs={p.isCapturingGraphs}
        captureContainerRef={p.captureContainerRef}
        handlePrint={p.handlePrint}
        handleDownloadClick={p.handleDownloadClick}
        handleDownloadClose={p.handleDownloadClose}
        handleDownloadPdf={p.handleDownloadPdf}
        handleDownloadWord={p.handleDownloadWord}
        handleSave={p.handleSave}
      />
    </Box>
  );
};

export default MathWordProblemsPageView;
